import {
  ButtonERP,
  DataTablePrimeReact,
  PaginationERP,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FacturaReadingSimpleDtoProps } from "api/DTOs/FacturaReadingSimpleDto"
import { RemesaDeFacturaCreationDtoProps } from "api/DTOs/RemesaDeFacturaCreationDto"
import {
  FacturaCarteraQueryParamsProps,
  getFacturaCartera,
  getFacturaCarteraImportePendiente,
  getFacturasByIdSimple,
} from "api/Factura.service"
import { createRemesaDeFactura } from "api/RemesaDeFactura.service"
import { TIPO_DOCUMENTO_ENUM } from "api/Types/TipoDocumentoEnum"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import axios from "axios"
import ActionToast from "common/components/ActionToast"
import ModalVerDocumento from "common/components/ModalVerDocumento/ModalVerDocumento"
import notification from "common/utils/notification"
import { requestHandler } from "common/utils/utils"
import { RESP_200 } from "constants/numbers"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { DEFAULT_CURRENCY_SYMBOL } from "features/Contactos/constants"
import { ImporteBodyTemplate } from "features/Documentos/templates"
import { NewRemesaFormData } from "features/Tesoreria/components/NewRemesaForm"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { frontendDateFormat } from "utils/formatDate"
import { precisionNumber } from "utils/precisionNumber"
import { FilterMenu } from "./FilterMenu"
import NewPagoFormModal from "./components/NewPagoFormModal"
import NewRemesaFormModal from "./components/NewRemesaFormModal"
import { TableCartera } from "./components/TableCartera"
import {
  CARTERA_COLUMNS,
  CarteraTableProps,
  PAYMENT_TYPE_CARTERA_ACTION_SETTINGS,
  adapterCarteraLibrador,
  getIdsFromDocumentos,
  getTotals,
} from "./constants"
import "./style.scss"

const Cartera = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const initialDate = [
    new Date(`${new Date().getFullYear()}-01-01`),
    new Date(),
  ]
  const [tabSelected, setTabSelected] = useState(
    PAYMENT_TYPE_CARTERA_ACTION_SETTINGS(t)[2].value
  )
  const [modalDocumentoDetail, setModalDocumentoDetail] = useState<{
    show: boolean
    document: FacturaReadingSimpleDtoProps | null
  }>({
    show: false,
    document: null,
  })
  const [dates, setDates] = useState<Date[] | undefined>(initialDate)
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const dtRef = useRef<DataTablePrimeReact<CarteraTableProps[]> | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }
  const [importePendiente, setImportePendiente] = useState<number | null>(null)
  const [documentSelected, setDocumentSelected] = useState<CarteraTableProps[]>(
    []
  )
  const [showNewRemesa, setShowNewRemesa] = useState<{
    data: NewRemesaFormData | null
    status: boolean
  }>({
    data: null,
    status: false,
  })
  const [show, setShow] = useState<{
    data: {
      empresaId?: string
      facturaId?: string
      fecha?: string
      importe?: number
      razonSocial?: string
      remesaId?: string
      tipo?: number
    } | null
    status: boolean
  }>({ data: null, status: false })
  const [toastValues, setToastValues] = useState<{
    total: number | null
    quantity: number | null
  }>({ total: 0, quantity: 0 })

  const handleRedirectToList = () => {
    history.push(ROUTES.REMESAS)
  }

  const facturaCarteraImportePendienteCancelToken = useRef(
    axios.CancelToken.source()
  )
  const facturaCarteraCancelToken = useRef(axios.CancelToken.source())
  const fetchItems = useCallback(() => {
    return (queryParams: FacturaCarteraQueryParamsProps) => {
      facturaCarteraImportePendienteCancelToken.current?.cancel()
      facturaCarteraImportePendienteCancelToken.current =
        axios.CancelToken.source()
      facturaCarteraCancelToken.current?.cancel()
      facturaCarteraCancelToken.current = axios.CancelToken.source()
      const params = {
        ...queryParams,
        TipoCartera: tabSelected,
        SkipCount: queryParams?.SkipCount ?? pagination.skipCount,
        MaxResultCount: queryParams?.MaxResultCount ?? pagination.itemsPerPage,
        SortingCriteria: queryParams.SortingCriteria,
      }

      setImportePendiente(null)
      getFacturaCarteraImportePendiente(
        params,
        facturaCarteraImportePendienteCancelToken.current.token
      ).then((importe) => setImportePendiente(importe))
      return getFacturaCartera(params, facturaCarteraCancelToken.current.token)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabSelected])

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchItems,
      serviceAdapter: adapterCarteraLibrador,
    })

  const clearRemesaSelection = useCallback(() => {
    setDocumentSelected([])
    setToastValues({ total: 0, quantity: 0 })
    history.replace()
  }, [history])

  const handleCreateModal = useCallback(
    (rowData: CarteraTableProps | null, status: boolean) => {
      setShow({
        data: {
          empresaId: rowData?.empresaId,
          razonSocial: rowData?.["librado.razonSocial"] ?? "",
          facturaId: rowData?.id,
          tipo: tabSelected,
          fecha: rowData?.fechaEmision,
          importe: rowData?.importePendiente,
        },
        status,
      })
    },
    [tabSelected]
  )
  const onSuccessCreate = async () => {
    fetchTableData()
  }
  const onSuccessCreateRemesa = async () => {
    handleRedirectToList()
  }
  const onPostCreate = () => {
    handleCreateModal(null, false)
    clearRemesaSelection()
  }

  const handleCreate = async (
    key: $TSMigrationFixMe,
    values: $TSMigrationFixMe
  ) => {
    requestHandler(
      values,
      PAYMENT_TYPE_CARTERA_ACTION_SETTINGS(t)[key].postService,
      onSuccessCreate,
      `${PAYMENT_TYPE_CARTERA_ACTION_SETTINGS(t)[key].title} ${t(
        "cartera.msg.createExit"
      )}.`,
      null,
      null,
      onPostCreate
    )
  }

  const handleCreateRemesa = async (
    values: RemesaDeFacturaCreationDtoProps
  ) => {
    try {
      const resp = await createRemesaDeFactura(values)
      if (resp.status === RESP_200) {
        onPostCreate()
        onSuccessCreateRemesa()
        setShowNewRemesa({ data: null, status: false })
        notification.success({
          body: String(t("cartera.msg.createTextExist")),
        })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error creando remesa:", error)
    }
  }

  const handleSelected = (rowsSelected: CarteraTableProps[]) => {
    const totalValue = getTotals(rowsSelected, "importePendiente")
    setToastValues({ total: totalValue, quantity: rowsSelected.length })
  }

  const handleOpenNewRemesa = (isOpen: boolean) => {
    const documentIds = getIdsFromDocumentos(documentSelected)
    setShowNewRemesa({
      data: {
        total: toastValues.total,
        facturasIds: documentIds,
        tipo: tabSelected,
      },
      status: isOpen,
    })
  }
  const handleTabChangeResetSelection = () => {
    setDocumentSelected([])
    setToastValues({ total: null, quantity: null })
  }

  const handleHideToast = () => {
    setDocumentSelected([])
    setToastValues({ total: null, quantity: null })
  }

  const handleOpenDocumentoModal = async (id: string) => {
    const facturaById = await getFacturasByIdSimple(id)
    setModalDocumentoDetail({
      show: true,
      document: facturaById,
    })
  }

  const onRenderContent = useMemo(() => {
    return (
      <div className='tw-flex tw-justify-between'>
        <span>
          {t("cartera.toast.selectedLabel")}{" "}
          <span className='tw-font-medium'>{toastValues?.quantity}</span>
        </span>
        <span className='tw-font-medium'>
          {t("cartera.toast.totalLabel")}{" "}
          <ImporteBodyTemplate
            // @ts-expect-error TS(2322): Type 'number' is not assignable to type 'string'.
            importe={toastValues?.total}
            divisa={DEFAULT_CURRENCY_SYMBOL}
          />
        </span>
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastValues])

  const onRenderCheckIcon = useCallback(
    (rowData: CarteraTableProps) => {
      return (
        <ButtonERP
          customize={{
            type: "white",
            size: "md",
          }}
          onClick={() => {
            handleCreateModal(rowData, true)
          }}
          icon={<FontAwesomeIcon icon={["fal", "coins"]} />}
        />
      )
    },
    [handleCreateModal]
  )

  const TableFooter = () => {
    return (
      <section
        className={`tw-flex tw-flex-row tw-items-center tw-px-6 tw-py-4 tw-bg-white ${
          dates ? "tw-justify-between" : "tw-justify-end"
        }`}
      >
        {dates && (
          <span className='tw-flex tw-text-base tw-font-medium tw-gap-2'>
            <p>{t("pagoCobro.labelDate")}</p>
            <span>
              {frontendDateFormat(dates?.[0])}
              {dates?.[1] && ` - ${frontendDateFormat(dates?.[1])}`}
            </span>
          </span>
        )}
        <span className='tw-flex tw-text-lg tw-font-medium tw-gap-2'>
          {t("pagoCobro.labelTotal")}
          <span className='tw-mr-4'>
            {`${
              importePendiente
                ? new Intl.NumberFormat("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(precisionNumber(importePendiente))
                : "-"
            } €`}
          </span>
        </span>
      </section>
    )
  }

  return (
    <>
      <section className='tw-flex tw-flex-col tw-order-2 tw-z-10 tw-relative tw-bg-white tw-w-full tw-max-h-screen tw-h-full tw-overflow-x-hidden tw-overflow-y-hidden'>
        <FilterMenu
          maxResultCount={pagination.itemsPerPage}
          sortingCriteria={sortingCriteria}
          resetPagination={handleResetPagination}
          serviceQuery={setServiceQuery}
          skipCount={pagination.skipCount}
          handleDates={{ dates, setDates }}
          handleTable={{ tabSelected, setTabSelected }}
          handleTabChangeResetSelection={handleTabChangeResetSelection}
        />
        <div className='tw-flex tw-flex-col tw-h-[calc(100%-150px)] tw-relative'>
          <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
            <TableCartera
              columns={CARTERA_COLUMNS(
                t,
                onRenderCheckIcon,
                handleOpenDocumentoModal,
                tabSelected
              )}
              refTable={dtRef}
              items={items}
              onSort={onSort}
              sortField={sortField}
              sortOrder={sortOrder}
              loading={isLoading}
              selection={documentSelected}
              onSelectionChange={({
                value,
              }: {
                value: CarteraTableProps[]
              }) => {
                setDocumentSelected(value)
                handleSelected(value)
              }}
            />
            <TableFooter />
            <PaginationERP
              className={PAGINATION_STYLES}
              totalRecords={totalCount}
              first={pagination.skipCount}
              rows={pagination.itemsPerPage}
              resetScroll={() => {
                dtRef.current && dtRef.current?.resetScroll()
              }}
              setPagination={setPagination}
              optionDropdown={[10, 20, 50, 100]}
              texts={{
                textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
                textCurrentPageReport: `${t("pagination.from")}`,
              }}
            />
          </div>
        </div>
        <NewPagoFormModal
          handleCreateModal={handleCreateModal}
          handleCreate={handleCreate}
          show={show}
        />

        <ActionToast
          show={Boolean(toastValues?.total)}
          onConfirm={() => handleOpenNewRemesa(!!toastValues?.total)}
          onHide={handleHideToast}
          content={onRenderContent}
          buttonLabel={t("cartera.toast.remittanceLabel")}
        />

        <NewRemesaFormModal
          showNewRemesa={showNewRemesa}
          handleOpenNewRemesa={handleOpenNewRemesa}
          handleCreateRemesa={handleCreateRemesa}
        />

        <ModalVerDocumento
          showModal={modalDocumentoDetail.show}
          onHide={() =>
            setModalDocumentoDetail({
              show: false,
              document: null,
            })
          }
          docType={TIPO_DOCUMENTO_ENUM.FACTURA}
          documentoID={modalDocumentoDetail.document?.id ?? null}
          typeDocumentTable='facturas'
        />
      </section>
    </>
  )
}

export default Cartera
